import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { AuditLogExplorer } from '@/components/users/AuditLogExplorer';
import { DEFAULT_AUDIT_FILTERS, type AuditFilters } from '@/features/users/auditLog';
import type { AuditSortColumn } from '@/features/users/hooks';

const SORT_COLUMNS: AuditSortColumn[] = ['created_at', 'action', 'entity_type', 'actor_name'];

/**
 * Filters live in the query string so an investigation can be bookmarked
 * or handed to someone else as a link, and so other pages (employee
 * profile, detail drawers) can deep-link into a pre-filtered view.
 */
function parseFilters(params: URLSearchParams): AuditFilters {
  const sortParam = params.get('sort') as AuditSortColumn | null;
  const column: AuditSortColumn =
    sortParam && SORT_COLUMNS.includes(sortParam) ? sortParam : 'created_at';
  const dirParam = params.get('dir');
  const dir: 'asc' | 'desc' =
    dirParam === 'asc' || dirParam === 'desc' ? dirParam : column === 'created_at' ? 'desc' : 'asc';
  const page = Number(params.get('page') ?? '1');
  const pageSize = Number(params.get('size') ?? DEFAULT_AUDIT_FILTERS.pageSize);
  return {
    search: params.get('q') ?? '',
    entityType: params.get('entity_type') ?? '',
    entityId: params.get('entity_id') ?? '',
    action: params.get('action') ?? '',
    actorId: params.get('actor') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    sort: { column, dir },
    page: Number.isFinite(page) && page >= 1 ? page - 1 : 0,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : DEFAULT_AUDIT_FILTERS.pageSize,
  };
}

function serializeFilters(f: AuditFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.search) p.set('q', f.search);
  if (f.entityType) p.set('entity_type', f.entityType);
  if (f.entityId) p.set('entity_id', f.entityId);
  if (f.action) p.set('action', f.action);
  if (f.actorId) p.set('actor', f.actorId);
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.sort.column !== 'created_at') p.set('sort', f.sort.column);
  if (f.sort.dir !== (f.sort.column === 'created_at' ? 'desc' : 'asc')) p.set('dir', f.sort.dir);
  if (f.page > 0) p.set('page', String(f.page + 1));
  if (f.pageSize !== DEFAULT_AUDIT_FILTERS.pageSize) p.set('size', String(f.pageSize));
  return p;
}

export function AuditLogPage() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(params), [params]);
  const onFiltersChange = useCallback(
    (next: AuditFilters) => setParams(serializeFilters(next), { replace: true }),
    [setParams],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        meta="Every create, update, and delete on audited tables, plus security events like role changes and MFA enrollment. Records who, when, from where, and exactly what changed. Append-only."
      />
      <AuditLogExplorer filters={filters} onFiltersChange={onFiltersChange} title="Events" />
    </div>
  );
}
